/**
 * SubmitDeveloperRegistrationCommand - Command and Handler for submitting developer registration
 * Follows MediatR pattern (Command + Handler in same file)
 */

import { logger } from '@/application/core';
import type { ICommand, ICommandHandler, IHttpClient } from '@/application/core';
import { toCamelCase } from '@/utils/caseTransform';
import type {
  DeveloperRegistrationFormData,
  SubmitDeveloperRegistrationResponse,
} from '@/application/queries/developerRegistration/developerRegistration.types';

// ============================================================================
// Command (Request)
// ============================================================================

/**
 * Command to submit developer registration form
 */
export class SubmitDeveloperRegistrationCommand implements ICommand<SubmitDeveloperRegistrationResponse> {
  readonly type = 'SubmitDeveloperRegistrationCommand' as const;

  constructor(
    public readonly formData: DeveloperRegistrationFormData,
    public readonly antiforgeryToken: string
  ) {}
}

// ============================================================================
// Handler
// ============================================================================

/**
 * Handler for SubmitDeveloperRegistrationCommand
 * Uses dependency injection for testability
 */
export class SubmitDeveloperRegistrationCommandHandler implements ICommandHandler<
  SubmitDeveloperRegistrationCommand,
  SubmitDeveloperRegistrationResponse
> {
  private readonly submitUrl = '/developer-registration-submit';

  constructor(private readonly httpClient: IHttpClient) {}

  async handle(
    command: SubmitDeveloperRegistrationCommand
  ): Promise<SubmitDeveloperRegistrationResponse> {
    try {
      // Convert form data to URLSearchParams for form-urlencoded format
      // This matches the legacy jQuery $.post() behavior
      const formData = new URLSearchParams();
      formData.append('CSRF-TOKEN-ECGRID-FORM', command.antiforgeryToken);
      formData.append('CompanyName', command.formData.companyName);
      formData.append('CompanyAddress', command.formData.companyAddress);
      formData.append('OwnerContactName', command.formData.ownerContactName);
      formData.append('OwnerContactEmail', command.formData.ownerContactEmail);

      // Optional fields
      if (command.formData.technicalContactName) {
        formData.append('TechnicalContactName', command.formData.technicalContactName);
      }
      if (command.formData.technicalContactEmail) {
        formData.append('TechnicalContactEmail', command.formData.technicalContactEmail);
      }
      if (command.formData.accessCode) {
        formData.append('AccessCode', command.formData.accessCode);
      }
      if (command.formData.programType) {
        formData.append('ProgramType', command.formData.programType);
      }

      // Make POST request with form-urlencoded data
      // Backend returns snake_case, we need to transform to camelCase
      // Use extended timeout because this endpoint involves Stripe + provisioning (~60-120s)
      const response = await this.httpClient.post<any>(this.submitUrl, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        timeout: 120000, // 120s - registration involves Stripe + provisioning
      });

      // Transform snake_case response -> camelCase for frontend
      return toCamelCase(response) as SubmitDeveloperRegistrationResponse;
    } catch (error) {
      // Transform error to expected response format
      logger.error('SubmitDeveloperRegistrationCommandHandler', 'Error:', error);

      return {
        success: false,
        errorMessage: 'An unexpected error occurred. Please try again.',
      };
    }
  }
}
